import type { DateTime } from 'luxon';

import type { Cost } from '@core/types/api/Cost/Cost';
import type { Currency } from '@core/types/api/Currency/Currency';
import Enums from '@core/types/api/Enums';
import { compareStringsAsc } from '@core/utils/strings';

import type { ListURLParams } from '../../../utils/ApiClient';

const compareNumbersAsc = (aVal: number, bVal: number): number => aVal - bVal;

export const BooleanSortValue = (value: boolean | null): number | null =>
  value === null ? null : Number(value);
export const DateTimeSortValue = (value: DateTime | null): number | null =>
  value?.toMillis() ?? null;
export const CurrencySortValue = (value: Currency | null): string | null =>
  value?.number?.replaceAll(',', '') ?? null;
export const CostSortValue = (value: Cost | null): string | null =>
  CurrencySortValue(value?.amount ?? null);

const sortComparator = <T extends { id: string }>(
  a: T,
  b: T,
  extractSortValue: (v: T) => string | number | null,
  order: Enums.SortDirection,
): number => {
  const aValue = extractSortValue(a);
  const bValue = extractSortValue(b);

  if (bValue === null || bValue === '') {
    return -1;
  }
  if (aValue === null || aValue === '') {
    return 1;
  }
  if (aValue === bValue) {
    return 0;
  }

  let ascResult: number;
  if (typeof aValue === 'number' && typeof bValue === 'number') {
    ascResult = compareNumbersAsc(aValue, bValue);
  } else {
    ascResult = compareStringsAsc(String(aValue), String(bValue));
  }
  return order === Enums.SortDirection.Ascending ? ascResult : -1 * ascResult;
};

export type SortConfig<TData extends { id: string }> = NonNullable<ListURLParams<TData>['sortBy']>;

type Comparator<TData extends {}> = (a: TData, b: TData) => number;

export const getComparator =
  <TData extends { id: string }>(
    direction: Enums.SortDirection,
    extractSortValue: (value: TData) => string | number | null,
  ): Comparator<TData> =>
  (a, b) =>
    sortComparator(a, b, extractSortValue, direction);
